import axios from 'axios';

const rawApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
const API_BASE_URL = rawApiUrl.replace(/\/$/, '').replace(/\/api$/i, '') + '/api';

let apiClient;

export const getApiClient = () => {
  if (!apiClient) {
    apiClient = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }
  return apiClient;
};

export const resetApiClient = () => {
  apiClient = undefined;
};

export const loginUser = async (userId, password) => {
  try {
    const response = await getApiClient().post('/login', { userId, password });
    const data = response.data;

    if (data.success && data.user && !data.user.location) {
      const usersData = await getUsers();
      const match = usersData.users?.find((u) => u.id === data.user.id);
      if (match?.location) {
        data.user.location = match.location;
      }
    }

    return data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const searchRecords = async (query) => {
  try {
    const response = await getApiClient().get('/search', { params: { query } });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const submitRecord = async (submission) => {
  try {
    const response = await getApiClient().post('/submit', submission);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getUsers = async () => {
  try {
    const response = await getApiClient().get('/users');
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const createUser = async (userData) => {
  try {
    const response = await getApiClient().post('/users', userData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const setUserPassword = async (userId, password, actorId) => {
  try {
    const response = await getApiClient().post(`/users/${userId}/password`, {
      password,
      actorId,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const updateUser = async (userId, userData) => {
  try {
    const response = await getApiClient().put(`/users/${userId}`, userData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getLocations = async () => {
  try {
    const response = await getApiClient().get('/locations');
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const saveLocations = async (locations) => {
  try {
    const response = await getApiClient().put('/locations', { locations });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const addLocation = async (name) => {
  try {
    const response = await getApiClient().post('/locations', { name });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getCompensationOptions = async (location) => {
  try {
    const response = await getApiClient().get('/compensation-options', {
      params: { location },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getCompensationConfig = async (location) => {
  try {
    const response = await getApiClient().get('/compensation-config', {
      params: location ? { location } : {},
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

const normalizeCompensationPayload = (items) =>
  (items || [])
    .map((item) => {
      if (typeof item === 'string') {
        return { option: item.trim(), active: true };
      }
      const option =
        typeof item?.option === 'string'
          ? item.option.trim()
          : typeof item?.option?.option === 'string'
            ? item.option.option.trim()
            : '';
      return {
        option,
        active: item?.active !== false && String(item?.active).toLowerCase() !== 'false',
      };
    })
    .filter((entry) => entry.option && entry.option !== '[object Object]');

export const saveCompensationConfig = async (location, items) => {
  try {
    const response = await getApiClient().put('/compensation-config', {
      location,
      options: normalizeCompensationPayload(items),
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getApprovingSupervisors = async () => {
  try {
    const response = await getApiClient().get('/approving-supervisors');
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const searchSeasonPasses = async (query) => {
  try {
    const response = await getApiClient().get('/season-passes/search', {
      params: { query },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getSeasonPassDetail = async (id) => {
  try {
    const response = await getApiClient().get(
      `/season-passes/${encodeURIComponent(id)}`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const createSeasonPass = async (seasonPass) => {
  try {
    const response = await getApiClient().post('/season-passes', seasonPass);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const updateSeasonPass = async (id, seasonPass) => {
  try {
    const response = await getApiClient().put(`/season-passes/${id}`, seasonPass);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const uploadSeasonPassPhoto = async (id, file) => {
  try {
    const formData = new FormData();
    formData.append('photo', file);
    const response = await getApiClient().post(
      `/season-passes/${encodeURIComponent(id)}/photo`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const deleteSeasonPassPhoto = async (id) => {
  try {
    const response = await getApiClient().delete(
      `/season-passes/${encodeURIComponent(id)}/photo`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getOrderProducts = async () => {
  try {
    const response = await getApiClient().get('/order-products');
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getProducts = async ({ all = false } = {}) => {
  try {
    const response = await getApiClient().get('/products', {
      params: all ? { all: '1' } : undefined,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const createProduct = async (payload) => {
  try {
    const response = await getApiClient().post('/products', payload);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const updateProduct = async (code, payload) => {
  try {
    const response = await getApiClient().put(
      `/products/${encodeURIComponent(code)}`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const lookupGateCredential = async (query) => {
  try {
    const response = await getApiClient().get('/gate/lookup', { params: { q: query } });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const logGateEntry = async (payload) => {
  try {
    const response = await getApiClient().post('/gate/entries', payload);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const uploadGateTicketPhoto = async (barcode, file) => {
  try {
    const formData = new FormData();
    formData.append('photo', file);
    const response = await getApiClient().post(
      `/gate/tickets/${encodeURIComponent(barcode)}/photo`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const uploadGateSeasonPassPhoto = async (id, file) => {
  try {
    const formData = new FormData();
    formData.append('photo', file);
    const response = await getApiClient().post(
      `/gate/season-passes/${encodeURIComponent(id)}/photo`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const cleanupDailyTicketPhotos = async (beforeDate) => {
  try {
    const response = await getApiClient().post('/gate/cleanup-daily-photos', {
      beforeDate,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const lookupParkingCredential = async (query) => {
  try {
    const response = await getApiClient().get('/parking/lookup', { params: { q: query } });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const searchOrders = async (query) => {
  try {
    const response = await getApiClient().get('/orders/search', { params: { query } });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getOrder = async (id) => {
  try {
    const response = await getApiClient().get(`/orders/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const createOrder = async (orderData) => {
  try {
    const response = await getApiClient().post('/orders', orderData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getCaseMeta = async () => {
  try {
    const response = await getApiClient().get('/cases/meta');
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getCases = async (params = {}) => {
  try {
    const response = await getApiClient().get('/cases', { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getCase = async (id) => {
  try {
    const response = await getApiClient().get(`/cases/${encodeURIComponent(id)}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const createCase = async (caseData) => {
  try {
    const response = await getApiClient().post('/cases', caseData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const updateCase = async (id, caseData) => {
  try {
    const response = await getApiClient().patch(`/cases/${encodeURIComponent(id)}`, caseData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const addCaseComment = async (id, commentData) => {
  try {
    const response = await getApiClient().post(
      `/cases/${encodeURIComponent(id)}/comments`,
      commentData
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const healthCheck = async () => {
  try {
    const response = await getApiClient().get('/health');
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCompanies = async () => {
  try {
    const response = await getApiClient().get('/companies');
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const createCompany = async (payload) => {
  try {
    const response = await getApiClient().post('/companies', payload);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const updateCompany = async (id, payload) => {
  try {
    const response = await getApiClient().put(`/companies/${encodeURIComponent(id)}`, payload);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getSubscriptions = async (companyId) => {
  try {
    const response = await getApiClient().get('/subscriptions', {
      params: companyId ? { companyId } : {},
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const createSubscription = async (payload) => {
  try {
    const response = await getApiClient().post('/subscriptions', payload);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const updateSubscription = async (id, payload) => {
  try {
    const response = await getApiClient().put(
      `/subscriptions/${encodeURIComponent(id)}`,
      payload
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

export const getPlatformMeta = async () => {
  try {
    const response = await getApiClient().get('/platform/meta');
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};
